import React, { useEffect, useState, useRef, useMemo } from "react";
import Navbar from "../../../common/navbar";
import Loader from "../../../common/loader";
import { useToast } from "../../../common/toastSystem";
import DateRangePicker from "../jobs/floatingLabelDateInput";
import SliderOptions from "../../adminProfiler/sliderOptions";

import { createPortal } from "react-dom";
import {
  Calendar,
  ChevronDown,
  Check,
  ArrowUpRight,
  ArrowDownRight,
  CalendarCog,
  ChevronRight,
  ChevronLeft,
  Info,
  TrendingUp,
  CircleCheckBig,
  CircleX,
  UserRoundSearch,
  UserRoundCheck,
  UserStar,
  Upload,
  Award,
  Medal,
  CircleUser,
  FileUser,
  ClipboardCheck,
  Briefcase,
  Send,
  Search,
  Download,
  Settings,
  Eye,
  EyeOff,
  Filter,
  MousePointer2,
  X,
} from "lucide-react";

import { PieChart, Pie, Cell } from "recharts";
import { RecruiterApi } from "../../../supports/recruiterApi";
import { generateRequestId } from "../../../common/common";
import unifiedAuthManager from "../../../services/unifiedAuthManager";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  FaBriefcase,
  FaUserFriends,
  FaPaperPlane,
  FaCheckCircle,
  FaUserPlus,
  FaTimesCircle,
  FaEnvelope,
  FaWhatsapp,
  FaPhoneAlt,
} from "react-icons/fa";

import XLSX from "xlsx-js-style";

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";

  // If an object is passed, extract the actual date string
  if (typeof dateStr === "object") {
    dateStr = dateStr.startDate || dateStr.endDate;
  }

  // Safety check
  if (typeof dateStr !== "string") return "";

  const [y, m, d] = dateStr.split("-");

  const dateObj = new Date(y, m - 1, d);
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = dateObj.toLocaleString("en-US", { month: "short" });
  const year = dateObj.getFullYear();

  return `${day} ${month} ${year}`;
};

const formatDateToISO = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`; // YYYY-MM-DD
};

// StatCard function component
function StatCard({ number, label, isHighlighted = false, rowNumber = 0 }) {
  return (
    <div
      className={`relative overflow-hidden group bg-white 2xl:p-6 xl:p-2 lg:p-2 md:p-1 sm:p-1 rounded-2xl hover:bg-[#f6f9f8]   hover:shadow-[0_0_7.9px_rgba(3,100,77,0.39)] shadow-[0_0_7.9px_rgba(0,0,0,0.07)] flex items-center min-h-[90px] max-h-[100px] transition-all duration-300`}
    >
      {/* THE STACKED SLANTED SHAPES */}

      <div className="absolute h-full left-2 top-4 bottom-4 w-16 bg-[#F2F7F6]  rounded-xl transform  z-0 opacity-60" />
      {/* Middle layer */}
      <div className="absolute h-full left-4 top-3 bottom-3 w-16 bg-[#E6F1EF] rounded-xl transform rotate-[13deg]  z-0" />

      {/* CONTENT CONTAINER */}
      <div className="relative z-10 flex items-center w-full">
        {/* Number Section - Fixed width to prevent overlap */}
        <div
          className={`flex-shrink-0 ${rowNumber === 1 && label === "Submitted to Client" ? "w-[5.5rem]" : "w-24"}`}
        >
          <span className="text-2xl 2xl:ml-0 xl:ml-2 text-center text-[#025c43] leading-none block font-['Raleway',sans-serif] font-extrabold">
            {number}
          </span>
        </div>

        {/* Label Section */}
        <div className="flex-grow">
          <p
            className={`${rowNumber === 3 ? "pl-2" : ""} font-raleway font-semibold text-[13px] leading-[14px] tracking-normal leading-snug}`}
          >
            {label}
          </p>
        </div>
      </div>
    </div>
  );
}

const capitalizeWords = (str) => {
  if (!str) return "";
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const LeaderBoard = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [appliedSearchText, setAppliedSearchText] = useState("");

  const [sortBy, setSortBy] = useState("submitted_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const [isSeachApiCalled, setIsSearchApiCalled] = useState(false);
  // Pagination state for API offset-based pagination
  const [offset, setOffset] = useState(0);
  const [apiTotalCandidates, setApiTotalCandidates] = useState(0);
  const [paginationMetadata, setPaginationMetadata] = useState({
    total_candidates: 0,
    limit: 10,
    offset: 0,
    next_offset: null,
    has_more: false,
  });

  const calendarRef = useRef(null);
  // Column visibility state - first 4 columns visible by default
  const [visibleColumns, setVisibleColumns] = useState({
    candidate: true,
    job_name: true,
    applicant_status: true,
    submitted_at: false,
    account_manager: false,
    sector: false,
    project: false,
    client: false,
  });

  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [expandStatusList, setExpandStatusList] = useState(false);
  const [expandAccountManagerList, setExpandAccountManagerList] = useState(false);
  const [expandProjectList, setExpandProjectList] = useState(false);
  const [expandClientList, setExpandClientList] = useState(false);

  const allColumns = [
    { key: "candidate", label: "Candidate", locked: true },
    { key: "job_name", label: "Job Name", locked: true },
    { key: "applicant_status", label: "Applicant Status", locked: true },
    { key: "submitted_at", label: "Submitted At", locked: false },
    { key: "account_manager", label: "Account Manager" },
    { key: "sector", label: "Sector", locked: false },
    { key: "project", label: "Project", locked: false },
    { key: "client", label: "Client", locked: false },
  ];

  // State for the Status sub-filter (now an array for multiple selections)
  const [statusFilter, setStatusFilter] = useState([]);
  const statusOptions = [
    "Applied",
    "Submitted for Approval",
    "Approved",
    "Submitted to Client",
    "Shortlisted",
    "Interview Scheduled",
    "Selected for Mob",
    "Placed",
    "Demobilized",
    "On Hold",
    "Required Clarification",
    "Rejected by AM/BD",
    "Rejected (not shortlisted)",
    "Rejected after Interview",
    "Rejected By Sourcer",
    "Candidate Declined",
    "Mob Cancelled",
  ];

  // Function to count items by status from API response
  const getStatusCount = (status) => {
    return selectedFilterCount[status] || 0;
  };

  // Function to toggle status selection
  const toggleStatusFilter = (status) => {
    setStatusFilter((prev) => {
      let newFilter;
      if (prev.includes(status)) {
        newFilter = prev.filter((s) => s !== status);
      } else {
        newFilter = [...prev, status];
      }
      // Call API with new filter
      setCurrentPage(1);
      setOffset(0);
      return newFilter;
    });
  };

  // Get total selected count
  const getTotalSelectedCount = () => {
    return apiTotalCandidates;
  };

  const toggleColumn = (key) => {
    if (allColumns.find((col) => col.key === key)?.locked) return;
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const dropdownRef = useRef(null);
  const dropdownKPIRef = useRef(null);
  const columnSettingsRef = useRef(null);
  const statusFilterRef = useRef(null);

  const [isKpiLoading, setIsKpiLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("Insights");
  const [isTableLoading, setIsTableLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setFilterOpen(false);
        setHoverOption(null);
      }
      if (
        dropdownKPIRef.current &&
        !dropdownKPIRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
      if (
        columnSettingsRef.current &&
        !columnSettingsRef.current.contains(event.target)
      ) {
        setShowColumnSettings(false);
      }
      if (
        statusFilterRef.current &&
        !statusFilterRef.current.contains(event.target)
      ) {
        setShowStatusFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeTab]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setFilterOpen(false);
        setHoverOption(null);
      }
      if (
        dropdownKPIRef.current &&
        !dropdownKPIRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
      if (
        columnSettingsRef.current &&
        !columnSettingsRef.current.contains(event.target)
      ) {
        setShowColumnSettings(false);
      }
      if (
        statusFilterRef.current &&
        !statusFilterRef.current.contains(event.target)
      ) {
        setShowStatusFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [applicantTableData, setApplicantTableData] = useState([]);
  const [filterApplicantData, setFilterApplicantData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilterCount, setSelectedFilterCount] = useState({});

  // Initialize with current month's date range
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return formatDateToISO(firstDay);
  });

  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    // Setting day to 0 gets the last day of the previous month
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return formatDateToISO(lastDay);
  });
  const [showCalendar, setShowCalendar] = useState(false);
  const user = unifiedAuthManager.getUnifiedUser();
  const recruiterId = user?.id;
  const [showFilters, setShowFilters] = useState(false);

  const now = new Date();
  const currentMonth = now.getMonth();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // State for KPI rows
  const [kpiData, setKpiData] = useState([]);
  const [row1, setRow1] = useState([]);
  const [row2, setRow2] = useState([]);
  const [row3, setRow3] = useState([]);

  // Transform API data into row format
  const transformKpiDataToRows = (performanceData) => {
    if (!performanceData || performanceData.length === 0) {
      // Return default empty rows
      return {
        row1: [
          { number: "0", label: "No. of vacancies" },
          { number: "0", label: "Submitted to AM" },
          { number: "0", label: "Approved by AM" },
          { number: "0", label: "Rejected by AM" },
          { number: "0", label: "Submitted to Client" },
        ],
        row2: [
          { number: "0", label: "Shortlisted", highlighted: true },
          { number: "0", label: "Forecasted Mob", highlighted: true },
          { number: "0", label: "Selected for Mob" },
          { number: "0", label: "Placed" },
        ],
        row3: [
          { number: "0.00", label: "Avg CV's sent to client" },
          { number: "0.00%", label: "CV's approved" },
          { number: "0.00%", label: "Success Rate" },
          { number: "0.00%", label: "Shortlist Percentage" },
        ],
      };
    }

    // Create a map of title to value for easy lookup
    const dataMap = {};
    performanceData.forEach((item) => {
      dataMap[item.title] = item.value;
    });

    const formatValue = (value, isPercentage = false, decimals = 0) => {
      if (value === null || value === undefined)
        return isPercentage ? "0.00%" : "0";

      const num = parseFloat(value);
      const hasDecimal = num !== Math.floor(num); // or !Number.isInteger(num)

      if (isPercentage) {
        return `${num.toFixed(decimals)}%`;
      }

      if (hasDecimal) {
        return num.toFixed(decimals);
      }

      return String(num);
    };

    const formatRatioAsPercentage = (value, decimals = 2) => {
      if (value === null || value === undefined) return "0.00%";

      const num = parseFloat(value);
      if (Number.isNaN(num)) return "0.00%";

      return `${(num * 100).toFixed(decimals)}%`;
    };

    return {
      row1: [
        {
          number: formatValue(dataMap["No.of Vacancies"]),
          label: "No. of vacancies",
        },
        {
          number: formatValue(dataMap["Submitted to AM"]),
          label: "Submitted to AM",
        },
        {
          number: formatValue(dataMap["Approved by AM"]),
          label: "Approved by AM",
        },
        {
          number: formatValue(dataMap["Rejected by AM"]),
          label: "Rejected by AM",
        },
        {
          number: formatValue(dataMap["Submitted to Client"]),
          label: "Submitted to Client",
        },
      ],
      row2: [
        {
          number: formatValue(dataMap["Shortlisted"]),
          label: "Shortlisted",
          highlighted: true,
        },
        {
          number: formatValue(dataMap["Forecasted Mob"]),
          label: "Forecasted Mob",
          highlighted: true,
        },
        {
          number: formatValue(dataMap["Selected for Mob"]),
          label: "Selected for Mob",
        },
        { number: formatValue(dataMap["Placed"]), label: "Placed" },
      ],
      row3: [
        {
          number: formatValue(dataMap["Avg Cv's sent to client"], false, 2),
          label: "Avg CV's sent to client",
        },
        {
          number: formatRatioAsPercentage(dataMap["% of Cv's approved"], 2),
          label: "CV's approved",
        },
        {
          number: formatRatioAsPercentage(dataMap["Success Rate"], 2),
          label: "Success Rate",
        },
        {
          number: formatRatioAsPercentage(dataMap["Shortlist Percentage"], 2),
          label: "Shortlist Percentage",
        },
      ],
    };
  };

  // Update rows when kpiData changes
  useEffect(() => {
    const {
      row1: newRow1,
      row2: newRow2,
      row3: newRow3,
    } = transformKpiDataToRows(kpiData);
    setRow1(newRow1);
    setRow2(newRow2);
    setRow3(newRow3);
  }, [kpiData]);
  const months = monthNames.slice(0, currentMonth + 1);
  const [selectedMonth, setSelectedMonth] = useState(monthNames[currentMonth]);
  const [open, setOpen] = useState(false);

  const [filterOpen, setFilterOpen] = useState(false);
  const [hoverOption, setHoverOption] = useState(null);

// sector filter state
  const [sectorFilter, setSectorFilter] = useState([]);
  const [expandSectorList, setExpandSectorList] = useState(false);
  const [sectorsCountMap, setSectorsCountMap] = useState({});

  // account manager / project / client filter state
  const [accountManagerFilter, setAccountManagerFilter] = useState([]);
  const [projectFilter, setProjectFilter] = useState([]);
  const [clientFilter, setClientFilter] = useState([]);
  const [accountManagersCountMap, setAccountManagersCountMap] = useState({});
  const [projectsCountMap, setProjectsCountMap] = useState({});
  const [clientsCountMap, setClientsCountMap] = useState({});

  // Fetch KPI data with default date range on component mount
  useEffect(() => {
    if (activeTab === "Insights" && (startDate || endDate) && recruiterId) {
      fetchKPIsByPeriod("date_range", null, { startDate, endDate });
    }
  }, [startDate, endDate, recruiterId, activeTab]);

  // Main effect for fetching table data when filters, search, pagination, or date changes
  // NOTE: sortBy/sortOrder are intentionally excluded — handleSort calls the API directly
  // with freshly computed values to avoid React state-staleness issues.
  useEffect(() => {
    if (activeTab === "Details" && (startDate || endDate) && recruiterId) {
      fetchApplicantTableData(pageSize, offset, statusFilter, sectorFilter, accountManagerFilter, projectFilter, clientFilter, sortBy, sortOrder);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearchText, activeTab, startDate, endDate, recruiterId, statusFilter, sectorFilter, accountManagerFilter, projectFilter, clientFilter, offset, pageSize]);

  // Effect to handle search input clearing - only updates appliedSearchText when search is cleared
  useEffect(() => {
    if (
      activeTab === "Details" &&
      isSeachApiCalled &&
      searchText.trim() === ""
    ) {
      // When search text is cleared after a search was applied, only update appliedSearchText
      // The above useEffect watching appliedSearchText will handle the API call
      setAppliedSearchText("");
      setCurrentPage(1);
      setOffset(0);
      setStatusFilter([]);
      setAccountManagerFilter([]);
      setProjectFilter([]);
      setClientFilter([]);
      setIsSearchApiCalled(false);
    }
  }, [searchText, activeTab, isSeachApiCalled]);

  const toast = useToast();



  const toggleSectorFilter = (sector) => {
  setSectorFilter(prev => {
    let newFilter;
    if (prev.includes(sector)) {
      newFilter = prev.filter(s => s !== sector);
    } else {
      newFilter = [...prev, sector];
    }
    // Trigger API call with updated sector filter
    setCurrentPage(1);
    setOffset(0);
    return newFilter;
  });
};


const getSectorCount = (sector) => {
  return sectorsCountMap[sector] || 0;
};

const toggleAccountManagerFilter = (am) => {
  setAccountManagerFilter(prev => {
    const newFilter = prev.includes(am) ? prev.filter(v => v !== am) : [...prev, am];
    setCurrentPage(1);
    setOffset(0);
    return newFilter;
  });
};

const toggleProjectFilter = (project) => {
  setProjectFilter(prev => {
    const newFilter = prev.includes(project) ? prev.filter(v => v !== project) : [...prev, project];
    setCurrentPage(1);
    setOffset(0);
    return newFilter;
  });
};

const toggleClientFilter = (client) => {
  setClientFilter(prev => {
    const newFilter = prev.includes(client) ? prev.filter(v => v !== client) : [...prev, client];
    setCurrentPage(1);
    setOffset(0);
    return newFilter;
  });
};

  const handleExportToExcel = (dataArray = []) => {
    let data = [];
    let title = "";
    if (activeTab === "Details") {
      // Only export visible columns
      data = dataArray.map((item) => {
        const rowData = {};

        if (visibleColumns.candidate) rowData.Candidate = item.candidate;
        if (visibleColumns.job_name) rowData["Job Name"] = item.job_name;
        if (visibleColumns.applicant_status)
          rowData["Applicant Status"] = item.applicant_status;
        if (visibleColumns.submitted_at)
          rowData["Submitted At"] = item.submitted_at || "Not Available";
        if (visibleColumns.account_manager)
          rowData["Account Manager"] = item.account_manager || "Not Available";
        if (visibleColumns.sector)
          rowData["Sector"] = item.sector || "Not Available";
        if (visibleColumns.project)
          rowData["Project"] = item.project || "Not Available";
        if (visibleColumns.client)
          rowData["Client"] = item.client || "Not Available";

        return rowData;
      });
      title = "Recruiter Dashboard Report";
    } else {
      // Format KPI data consistently with the dashboard cards.
      const ratioPercentageFields = [
        "% of Cv's approved",
        "Success Rate",
        "Shortlist Percentage",
      ];

      data = kpiData.map((item) => ({
        ...item,
        title:
          item.title === "% of Cv's approved"
            ? "CV's approved"
            : item.title,
        value:
          item.title === "Avg Cv's sent to client" &&
          typeof item.value === "number"
            ? parseFloat(item.value).toFixed(2)
            : ratioPercentageFields.includes(item.title) &&
              typeof item.value === "number"
            ? `${(parseFloat(item.value) * 100).toFixed(2)}%`
            : item.value,
      }));
      title = "Recruiter KPI Report";
    }

    if (data.length === 0) {
      toast.warning("No data available to export.");
      return;
    }
    const wb = XLSX.utils.book_new();
    const keys = Object.keys(data[0]);
    const numCols = keys.length;

    // 1. Prepare formatted Header Names (CamelCase to Title Case)
    const headerNames = keys.map((key) =>
      key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim(),
    );

    // 2. Create the worksheet starting with the data at A3
    // We pass null for headers initially so we can manually style them
    const ws = XLSX.utils.json_to_sheet(data, {
      origin: "A3",
      skipHeader: false,
    });

    // 3. Add the Main Title at A1
    XLSX.utils.sheet_add_aoa(ws, [[title.toUpperCase()]], { origin: "A1" });

    // 4. Manual Header Overwrite (to apply Title Case names)
    XLSX.utils.sheet_add_aoa(ws, [headerNames], { origin: "A3" });

    // 5. Define Styles
    const styles = {
      title: {
        font: { bold: true, sz: 16, color: { rgb: "#004645;" } },
        fill: { fgColor: { rgb: "b0d3d2" } },
        alignment: { horizontal: "center", vertical: "center" },
      },
      header: {
        font: { bold: true, color: { rgb: "#004645;" } },
        fill: { fgColor: { rgb: "b0d3d2" } },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
        },
        alignment: { horizontal: "center" },
      },
      evenRow: {
        fill: { fgColor: { rgb: "E6F4F1" } }, // Light blue stripe
        border: { bottom: { style: "thin", color: { rgb: "D9D9D9" } } },
      },
      oddRow: {
        fill: { fgColor: { rgb: "FFFFFF" } },
        border: { bottom: { style: "thin", color: { rgb: "D9D9D9" } } },
      },
    };

    // 6. Apply Styles to Cells
    const range = XLSX.utils.decode_range(ws["!ref"]);

    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellRef]) continue;

        // Title Row (A1)
        if (R === 0) {
          ws[cellRef].s = styles.title;
        }
        // Header Row (A3)
        else if (R === 2) {
          ws[cellRef].s = styles.header;
        }
        // Data Rows (A4 onwards)
        else if (R > 2) {
          ws[cellRef].s = R % 2 === 0 ? styles.evenRow : styles.oddRow;
        }
      }
    }

    // 7. Merge Title Cells
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } }];

    // 8. Auto-size Columns
    ws["!cols"] = keys.map((key, i) => {
      const maxDataLength = Math.max(
        headerNames[i].length,
        ...data.map((row) => String(row[key] ?? "").length),
      );
      return { wch: maxDataLength + 5 };
    });

    // 9. Export
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    const now = new Date();
    const date = now.toLocaleDateString("en-CA"); // 'en-CA' outputs YYYY-MM-DD

    // 2. Get the hh:mmam/pm part
    const time = now
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase()
      .replace(/\s+/g, ""); // Removes the space (e.g., "12:24 pm" -> "12:24pm")

    // 3. Combine with a hyphen
    const timestamp = `${date}-${time}`;
    const fileName = `${title.replace(/\s+/g, "_")}_${
      user.displayName
    }_${timestamp}.xlsx`;

    XLSX.writeFile(wb, fileName);
  };

  const fetchApplicantTableData = async (
    paginationLimit = pageSize,
    paginationOffset = offset,
    filters = statusFilter,
    sectorFilters = sectorFilter,
    amFilters = accountManagerFilter,
    projFilters = projectFilter,
    clientFilters = clientFilter,
    sb = sortBy,
    so = sortOrder
  ) => {
    if (paginationLimit !== null && paginationOffset !== null) {
      setIsTableLoading(true);
    }

    try {
      const requestBody = {
        recruiter_id: recruiterId,
        limit: paginationLimit,
        offset: paginationOffset,
        search_text: appliedSearchText,
        sort_by: sb,
        sort_order: so,
        user_payload: {
          request_id: generateRequestId(),
          email: user.email,
          tenant: 101,
          tenant_id: 101,
          user_id: "010",
          role: "recruiter",
        },
      };

      // Add filter parameter if filters are provided
      if (filters && filters.length > 0) {
        requestBody.filter = JSON.stringify(filters);
      }

      if (sectorFilters && sectorFilters.length > 0) {
        requestBody.sector_filter = JSON.stringify(sectorFilters);
      }

      if (amFilters && amFilters.length > 0) {
        requestBody.account_manager_filter = JSON.stringify(amFilters);
      }

      if (projFilters && projFilters.length > 0) {
        requestBody.project_filter = JSON.stringify(projFilters);
      }

      if (clientFilters && clientFilters.length > 0) {
        requestBody.client_filter = JSON.stringify(clientFilters);
      }

      if (startDate && endDate) {
        requestBody.start_date = startDate;
        requestBody.end_date = endDate;
      } else if (startDate) {
        requestBody.start_date = startDate;
        requestBody.end_date = startDate;
      }

      const response =
        await RecruiterApi.fetch_leaderboard_table_data(requestBody);
      console.log("Fetched applicant table data:", response);
      if (response.status_code === 200) {
        const data = response.data;

        if (paginationLimit === null && paginationOffset === null) {
          handleExportToExcel(data?.candidates || []);
        } else {
          if (appliedSearchText.trim() !== "") {
            setIsSearchApiCalled(true);
          } else {
            setIsSearchApiCalled(false);
          }
          setApplicantTableData(data?.candidates || []);
          setFilterApplicantData(data?.candidates || []);
          setSectorsCountMap(data?.sectors_count || {});
          setAccountManagersCountMap(data?.account_managers_count || {});
          setProjectsCountMap(data?.projects_count || {});
          setClientsCountMap(data?.clients_count || {});
          // Store the selected_filter_count from API response
          setSelectedFilterCount(data?.all_filter_count || {});

          // Store the total candidates count from API
          const totalCandidates = data?.total_candidates || 0;
          setApiTotalCandidates(totalCandidates);

          // Update pagination metadata from API response
          setPaginationMetadata({
            total_candidates: totalCandidates,
            limit: data?.limit || paginationLimit,
            offset: data?.offset || paginationOffset,
            next_offset: data?.next_offset || null,
            has_more: data?.has_more || false,
          });
        }
      } else {
        setIsSearchApiCalled(false);
        toast.error(response.message);
      }
    } catch (error) {
      setIsSearchApiCalled(false);
      console.error("Error fetching applicant table data:", error);
    } finally {
      setIsTableLoading(false);
    }
  };

  // Consolidated filtering logic
  const filteredData = useMemo(() => {
    return applicantTableData.filter((item) => {
      const searchLower = appliedSearchText.toLowerCase();
      const matchesSearch =
        !appliedSearchText ||
        item.candidate?.toLowerCase().includes(searchLower) ||
        item.job_name?.toLowerCase().includes(searchLower);

      // If no status filter selected, show all. Otherwise, check if item status is in selected statuses
      const matchesStatus =
        statusFilter.length === 0 ||
        statusFilter.includes(item.applicant_status);

      return matchesSearch && matchesStatus;
    });
  }, [applicantTableData, appliedSearchText, statusFilter]);

  // Check if any client-side filters are applied

  // Pagination logic:
  // - If no client filters: use server-side total_candidates for pagination
  // - If client filters: use client-side filtered data for pagination
  const totalForPagination = apiTotalCandidates;
  const totalPages = Math.ceil(totalForPagination / pageSize);
  // Reset search handler
  const handleResetSearch = () => {
    setSearchText("");
    // Setting appliedSearchText to empty will trigger the useEffect which calls the API
    setAppliedSearchText("");
    setCurrentPage(1);
    setOffset(0);
    setSortBy("submitted_at");
    setSortOrder("desc");
    setStatusFilter([]);
    setAccountManagerFilter([]);
    setProjectFilter([]);
    setClientFilter([]);
    setIsSearchApiCalled(false);
  };

  const handleSort = (columnKey) => {
    let newOrder = "desc";
    if (sortBy === columnKey) {
      newOrder = sortOrder === "asc" ? "desc" : "asc";
    } else {
      newOrder = "asc";
    }
    setSortBy(columnKey);
    setSortOrder(newOrder);
    setCurrentPage(1);
    setOffset(0);
    // Call directly with the freshly computed values so state staleness is bypassed
    fetchApplicantTableData(pageSize, 0, statusFilter, sectorFilter, accountManagerFilter, projectFilter, clientFilter, columnKey, newOrder);
  };

  // For data display, use filtered data for client-side filters, otherwise use current page data
  const currentPageData = applicantTableData;

  const setLoadingStates = (states, value) => {
    states.forEach((fn) => fn(value));
  };
  const updateApplicantStatus = (applicantId, newStatus) => {
    setApplicantTableData((prevData) =>
      prevData.map((item) =>
        item.id === applicantId
          ? { ...item, applicant_status: newStatus }
          : item,
      ),
    );
    toast.success(`Status updated to: ${capitalizeWords(newStatus)}`);
  };

  const fetchKPIsByPeriod = async (
    periodType,
    periodValue,
    start = null,
    end = null,
  ) => {
    setIsKpiLoading(true);
    let filterType, filterValue;

    if (periodType === "Month") {
      filterType = "month";
      filterValue = periodValue; // e.g., "2026-January"
    } else if (periodType === "Quarter") {
      filterType = "quarter";
      filterValue = periodValue; // e.g., "2026-Q1"
    } else if (periodType === "Year") {
      filterType = "year";
      filterValue = periodValue; // e.g., "2026"
    } else {
      // Fallback for Custom date range (when periodType is null)
      filterType = "date_range";
      // const startDate = new Date(start);
      // const endDate = new Date(end);
      filterValue = JSON.stringify({
        start: start.startDate,
        end: start.endDate,
      });
    }

    const requestBody = {
      recruiter_id: recruiterId,
      recruiter_email: user.email,
      filter_type: filterType,
      filter_value: filterValue,
      user_payload: {
        request_id: generateRequestId(),
        email: user.email,
        tenant: 101,
        tenant_id: 101,
        user_id: "010",
      },
    };

    try {
      console.log("Fetching KPIs with request body:", requestBody);
      const response = await RecruiterApi.fetchKPIs(requestBody);
      if (response.status_code === 200) {
        setKpiData(response.data?.performance || []);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error fetching KPIs:", error);
    } finally {
      setIsKpiLoading(false);
    }
  };

  return (
    <div
      className={`${activeTab === "Insights" ? "main-background" : "bg-white"} flex flex-col h-screen pt-16`}
    >
      <style>{`
       .no-scrollbar::-webkit-scrollbar {

    display: none;

  }

  .no-scrollbar {

    -ms-overflow-style: none;

    scrollbar-width: none;

  }



  /* Custom Scrollbar for the Table Container */

  .custom-table-scrollbar::-webkit-scrollbar {

    width: 6px;  /* Vertical scrollbar width */

    height: 5px; /* Horizontal scrollbar height - slimmer and lighter */

  }



  .custom-table-scrollbar::-webkit-scrollbar-track {

    background: #f9fafb; /* Extremely light track */

    border-radius: 10px;

  }



  .custom-table-scrollbar::-webkit-scrollbar-thumb {

    background: #e5e7eb; /* Light gray thumb */

    border-radius: 10px;

    transition: background 0.2s ease;

  }



  .custom-table-scrollbar::-webkit-scrollbar-thumb:hover {

    background: #d1d5db; /* Slightly darker on hover for intuition */

  }



  /* Target only horizontal scrollbar for extra lightness */

  .custom-table-scrollbar::-webkit-scrollbar:horizontal {

    background-color: transparent;

  }
      `}</style>
      <Navbar />
      {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loader message="Loading dashboard" size={120} />
        </div>
      ) : (
        <>
          {activeTab !== "overview" && (
            <div className="min-h-screen  font-sans px-2 pt-8 relative overflow-hidden">
              {/* Background Decoration (Simulating the blurred office background) */}
              <div className="absolute inset-0 opacity-5 pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-gray-400 rounded-full blur-3xl" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-gray-400 rounded-full blur-3xl" />
              </div>

              <div className="mx-auto relative z-10">
                {/* Header Navigation */}
                <div className="flex px-8 flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="shrink-0">
                    <SliderOptions
                      options={["Insights", "Details"]}
                      theme="theme1"
                      defaultValue={activeTab}
                      onChange={(value) => {
                        setActiveTab(value);
                      }}
                    />
                  </div>
                  {/* Middle: Animated Search Bar (Tailwind Only Animation) */}
                  <div
                    className={`
      flex-1 transition-all duration-500 ease-in-out overflow-hidden
      ${
        activeTab === "Details"
          ? "opacity-100 max-w-[500px] translate-y-0"
          : "opacity-0 max-w-0 -translate-y-2 pointer-events-none"
      }
    `}
                  >
                    <div className="relative w-72 md:w-96 lg:w-[28rem] text-xs px-2 py-1">
                      <input
                        type="text"
                        placeholder="Search by Candidate/Job"
                        value={searchText}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setSearchText(newValue);

                          // If search is cleared completely and there was an active search, trigger reset
                          if (newValue.trim() === "" && isSeachApiCalled) {
                            // Trigger search clear - the useEffect will handle the API call
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            searchText.trim() !== "" &&
                              setAppliedSearchText(searchText.trim());
                          }
                        }}
                        className="w-full pl-8 pr-10 py-1.5 text-sm border border-gray-200 rounded-full"
                      />
                      <Search
                        className="absolute ml-1 left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        size={18}
                      />
                      {(searchText.trim() !== "" || isSeachApiCalled) && (
                        <button
                          onClick={handleResetSearch}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Reset search"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <button
                        disabled={isKpiLoading}
                        type="button"
                        onClick={() => setShowCalendar((prev) => !prev)}
                        className="px-3 py-2 border rounded-lg hover:bg-gray-100 flex items-center gap-2 text-xs"
                      >
                        <Calendar size={16} className="mph-primary" />
                        {startDate && endDate ? (
                          <span>
                            {formatDisplayDate(startDate)} →{" "}
                            {formatDisplayDate(endDate)}
                          </span>
                        ) : startDate ? (
                          <span>{formatDisplayDate(startDate)}</span>
                        ) : (
                          <span className="text-gray-400">Select Dates</span>
                        )}
                      </button>

                      {showCalendar &&
                        createPortal(
                          <div
                            ref={calendarRef}
                            className="fixed top-0 left-0 w-full h-full flex items-center justify-center z-[9999]"
                            onClick={() => setShowCalendar(false)}
                          >
                            <div
                              className="bg-white shadow-2xl mx-4 absolute top-[8.5rem] right-[1rem]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <DateRangePicker
                                selectedStart={startDate}
                                selectedEnd={endDate}
                                emitDefaultOnMount={true}
                                onChange={({ startDate: s, endDate: e }) => {
                                  setShowCalendar(false);
                                  setStartDate(s);
                                  setEndDate(e);

                                  // if (s && e) {
                                  //   fetchKPIsByPeriod(null, null, s, e);
                                  // } else if (s) {
                                  //   fetchKPIsByPeriod(null, null, s, s);
                                  // }
                                }}
                              />
                            </div>
                          </div>,
                          document.body,
                        )}
                    </div>

                    <button
                      onClick={() => fetchApplicantTableData(null, null)}
                      className="p-1.5 bg-[#a6834d] text-white rounded-md shadow-md hover:bg-[#8e6f41] transition-colors"
                    >
                      <Download size={18} />
                    </button>
                  </div>
                </div>
                {activeTab === "Insights" && (
                  <div className="px-8 ">
                    {/* Hero Title */}
                    <div className="text-center 2xl:mt-4 2xl:mb-16 xl:mb-8 lg:mb-8 md:mb-4">
                      <h1
                        className="pb-1 text-[1.375rem] leading-[24px] font-raleway font-semibold  bg-gradient-to-b
  from-[#E0AC5A]
  to-[#855E20]
  bg-clip-text
  text-transparent"
                      >
                        Your recruitment intelligence, in one place
                      </h1>
                      <p className="text-[#a6834d] text-base">
                        A real-time snapshot of your hiring performance.
                      </p>
                    </div>

                    {/* Stats Grid */}
                    <div className="2xl:space-y-8 xl:space-y-6 lg:space-y-6 md:space-y-4 sm-space-y-4">
                      {isKpiLoading ? (
                        <div className="flex h-48 items-center justify-center">
                          <Loader message="Loading KPIs" size={60} />
                        </div>
                      ) : (
                        <>
                          {/* Row 1 */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 2xl:gap-8 xl:gap-6 lg:gap-6 md:gap-4 sm-gap-2">
                            {row1.map((stat, i) => (
                              <StatCard
                                key={i}
                                {...stat}
                                isHighlighted={stat.highlighted}
                                rowNumber={1}
                              />
                            ))}
                          </div>

                          {/* Row 2 */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 2xl:gap-8 xl:gap-6 lg:gap-6 md:gap-4 sm-gap-2">
                            {row2.map((stat, i) => (
                              <StatCard key={i} {...stat} />
                            ))}
                          </div>

                          {/* Row 3 - Centered on large screens */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 2xl:gap-8 xl:gap-6 lg:gap-6 md:gap-4 sm-gap-2  mx-auto">
                            {row3.map((stat, i) => (
                              <StatCard key={i} {...stat} rowNumber={3} />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {activeTab === "Details" && (
                  <>
                    {isTableLoading ? (
                      <div className="flex h-96 items-center justify-center">
                        <Loader message="Loading" size={80} />
                      </div>
                    ) : (
                      <>
                        <div className="flex h-screen bg-white overflow-hidden mt-2">
                          {/* LEFT SIDEBAR FILTERS */}
                          {showFilters && (
                            <div className="w-64 bg-white flex flex-col transition-all duration-300">
                              <div className="p-1 border-gray-100 flex items-center gap-2 font-semibold text-gray-700">
                                <button
                                  onClick={() => setShowFilters(!showFilters)}
                                  className={`p-1.5 rounded-md transition-colors profiler-bg text-white`}
                                >
                                  <Filter size={16} />
                                </button>
                                <span className="text-xs font-semibold text-gray-900">
                                  Filters
                                </span>
                              </div>

                              <div className="flex-1 overflow-y-auto p-2 space-y-1 max-h-[calc(100vh-185px)]">
                                {allColumns.map((col) => (
                                  <div key={col.key}>
                                    {/* Main Column Toggle */}
                                    <div
                                      onClick={() =>
                                        !col.locked && toggleColumn(col.key)
                                      }
                                      className={`flex items-center gap-3 px-3 py-1.5 rounded-md transition-colors 
                    ${col.locked ? "bg-gray-50 cursor-not-allowed opacity-70" : "hover:bg-[#B2853D33] cursor-pointer"}
                    ${visibleColumns[col.key] ? "text-yellow-600 font-medium bg-[#B2853D14]" : "text-gray-500"}
                  `}
                                    >
                                      {visibleColumns[col.key] ? (
                                        <Check
                                          size={12}
                                          className="text-amber-600 font-bold"
                                        />
                                      ) : (
                                        <Check
                                          size={12}
                                          className="text-gray-600 font-bold"
                                        />
                                      )}

                                       <span className="text-[11px] flex-1">
        {col.label}
        {/* Count Badge for Sector */}
        {col.key === 'sector' && sectorFilter.length > 0 && (
          <span className="ml-2 px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full text-[9px] font-semibold">
            {sectorFilter.length}
          </span>
        )}
        {/* Count Badge for Status */}
        {col.key === 'applicant_status' && statusFilter.length > 0 && (
          <span className="ml-2 px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full text-[9px] font-semibold">
            {statusFilter.length}
          </span>
        )}
        {/* Count Badge for Account Manager */}
        {col.key === 'account_manager' && accountManagerFilter.length > 0 && (
          <span className="ml-2 px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full text-[9px] font-semibold">
            {accountManagerFilter.length}
          </span>
        )}
        {/* Count Badge for Project */}
        {col.key === 'project' && projectFilter.length > 0 && (
          <span className="ml-2 px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full text-[9px] font-semibold">
            {projectFilter.length}
          </span>
        )}
        {/* Count Badge for Client */}
        {col.key === 'client' && clientFilter.length > 0 && (
          <span className="ml-2 px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full text-[9px] font-semibold">
            {clientFilter.length}
          </span>
        )}
      </span>
                                      {/* <span className="text-[11px] flex-1">
                                        {col.label}
                                        {col.key === "applicant_status" &&
                                          statusFilter.length > 0 && (
                                            <span className="ml-2 px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full text-[9px] font-semibold">
                                              {getTotalSelectedCount()}
                                            </span>
                                          )}
                                      </span> */}
                                      {visibleColumns[col.key] && col.locked}


  {/* NEW: Chevron for Sector Toggle */}
      {col.key === 'sector' && visibleColumns.sector && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpandSectorList(!expandSectorList);
          }}
          className="ml-auto p-1 rounded transition-colors hover:text-yellow-800"
        >
          <ChevronDown 
            size={14} 
            className={`transition-transform duration-300 ${expandSectorList ? 'rotate-180' : ''}`}
          />
        </button>
      )}

                                      {/* Down Arrow for Status Filter Toggle */}
                                      {col.key === "applicant_status" &&
                                        visibleColumns.applicant_status && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setExpandStatusList(
                                                !expandStatusList,
                                              );
                                            }}
                                            className="ml-auto p-1  rounded transition-colors hover:text-yellow-800"
                                          >
                                            <ChevronDown
                                              size={14}
                                              className={`transition-transform duration-300 ho ${expandStatusList ? "rotate-180" : ""}`}
                                            />
                                          </button>
                                        )}

                                      {/* Chevron for Account Manager filter */}
                                      {col.key === 'account_manager' && visibleColumns.account_manager && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setExpandAccountManagerList(!expandAccountManagerList); }}
                                          className="ml-auto p-1 rounded transition-colors hover:text-yellow-800"
                                        >
                                          <ChevronDown size={14} className={`transition-transform duration-300 ${expandAccountManagerList ? 'rotate-180' : ''}`} />
                                        </button>
                                      )}

                                      {/* Chevron for Project filter */}
                                      {col.key === 'project' && visibleColumns.project && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setExpandProjectList(!expandProjectList); }}
                                          className="ml-auto p-1 rounded transition-colors hover:text-yellow-800"
                                        >
                                          <ChevronDown size={14} className={`transition-transform duration-300 ${expandProjectList ? 'rotate-180' : ''}`} />
                                        </button>
                                      )}

                                      {/* Chevron for Client filter */}
                                      {col.key === 'client' && visibleColumns.client && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setExpandClientList(!expandClientList); }}
                                          className="ml-auto p-1 rounded transition-colors hover:text-yellow-800"
                                        >
                                          <ChevronDown size={14} className={`transition-transform duration-300 ${expandClientList ? 'rotate-180' : ''}`} />
                                        </button>
                                      )}
                                    </div>

                                     {/* SECTOR SUB-FILTER LIST */}
    {col.key === 'sector' && visibleColumns.sector && expandSectorList && (
      <div className="ml-8 mt-1 space-y-1 border-l-2 border-amber-100 pl-2 mb-2">
        {Object.keys(sectorsCountMap).length > 0 ? (
          Object.keys(sectorsCountMap).map(option => (
            <button
              key={option}
              onClick={() => toggleSectorFilter(option)}
              className={`flex items-center justify-between w-full text-left px-2 py-1 text-xs rounded transition-colors
                ${sectorFilter.includes(option) ? 'bg-amber-100 text-amber-800' : 'text-gray-500 hover:text-gray-800'}
              `}
            >
              <span className="flex items-center gap-2">
                {sectorFilter.includes(option) ? (
                  <Check size={12} className="text-amber-600 font-bold flex-shrink-0" />
                ) : (
                  <div className="w-3 h-3 flex-shrink-0" />
                )}
                {option}
              </span>
              <span className="text-gray-400 text-[10px] font-medium">
                {getSectorCount(option)}
              </span>
            </button>
          ))
        ) : (
          <div className="text-[10px] text-gray-400 px-2">No sectors available</div>
        )}
      </div>
    )}

                                    {/* STATUS SUB-FILTER */}
                                    {col.key === "applicant_status" &&
                                      visibleColumns.applicant_status &&
                                      expandStatusList && (
                                        <div className="ml-8 mt-1 space-y-1 border-l-2 border-amber-100 pl-2 mb-2">
                                          {statusOptions.map((option) => (
                                            <button
                                              key={option}
                                              onClick={() =>
                                                toggleStatusFilter(option)
                                              }
                                              className={`flex items-center justify-between w-full text-left px-2 py-1 text-xs rounded transition-colors
                          ${statusFilter.includes(option) ? "bg-amber-100 text-amber-800" : "text-gray-500 hover:text-gray-800"}
                        `}
                                            >
                                              <span className="flex items-center gap-2">
                                                {statusFilter.includes(option) ? (
                                                  <Check size={12} className="text-amber-600 font-bold flex-shrink-0" />
                                                ) : (
                                                  <div className="w-3 h-3 flex-shrink-0" />
                                                )}
                                                {option}
                                              </span>
                                              <span className="text-gray-400 text-[10px] font-medium">
                                                {getStatusCount(option)}
                                              </span>
                                            </button>
                                          ))}
                                        </div>
                                      )}

                                    {/* ACCOUNT MANAGER SUB-FILTER */}
                                    {col.key === 'account_manager' && visibleColumns.account_manager && expandAccountManagerList && (
                                      <div className="ml-8 mt-1 space-y-1 border-l-2 border-amber-100 pl-2 mb-2">
                                        {Object.keys(accountManagersCountMap).length > 0 ? (
                                          Object.keys(accountManagersCountMap).map(option => (
                                            <button
                                              key={option}
                                              onClick={() => toggleAccountManagerFilter(option)}
                                              className={`flex items-center justify-between w-full text-left px-2 py-1 text-xs rounded transition-colors
                                                ${accountManagerFilter.includes(option) ? 'bg-amber-100 text-amber-800' : 'text-gray-500 hover:text-gray-800'}
                                              `}
                                            >
                                              <span className="flex items-center gap-2">
                                                {accountManagerFilter.includes(option) ? (
                                                  <Check size={12} className="text-amber-600 font-bold flex-shrink-0" />
                                                ) : (
                                                  <div className="w-3 h-3 flex-shrink-0" />
                                                )}
                                                {option}
                                              </span>
                                              <span className="text-gray-400 text-[10px] font-medium">
                                                {accountManagersCountMap[option]}
                                              </span>
                                            </button>
                                          ))
                                        ) : (
                                          <div className="text-[10px] text-gray-400 px-2">No account managers available</div>
                                        )}
                                      </div>
                                    )}

                                    {/* PROJECT SUB-FILTER */}
                                    {col.key === 'project' && visibleColumns.project && expandProjectList && (
                                      <div className="ml-8 mt-1 space-y-1 border-l-2 border-amber-100 pl-2 mb-2">
                                        {Object.keys(projectsCountMap).length > 0 ? (
                                          Object.keys(projectsCountMap).map(option => (
                                            <button
                                              key={option}
                                              onClick={() => toggleProjectFilter(option)}
                                              className={`flex items-center justify-between w-full text-left px-2 py-1 text-xs rounded transition-colors
                                                ${projectFilter.includes(option) ? 'bg-amber-100 text-amber-800' : 'text-gray-500 hover:text-gray-800'}
                                              `}
                                            >
                                              <span className="flex items-center gap-2">
                                                {projectFilter.includes(option) ? (
                                                  <Check size={12} className="text-amber-600 font-bold flex-shrink-0" />
                                                ) : (
                                                  <div className="w-3 h-3 flex-shrink-0" />
                                                )}
                                                {option}
                                              </span>
                                              <span className="text-gray-400 text-[10px] font-medium">
                                                {projectsCountMap[option]}
                                              </span>
                                            </button>
                                          ))
                                        ) : (
                                          <div className="text-[10px] text-gray-400 px-2">No projects available</div>
                                        )}
                                      </div>
                                    )}

                                    {/* CLIENT SUB-FILTER */}
                                    {col.key === 'client' && visibleColumns.client && expandClientList && (
                                      <div className="ml-8 mt-1 space-y-1 border-l-2 border-amber-100 pl-2 mb-2">
                                        {Object.keys(clientsCountMap).length > 0 ? (
                                          Object.keys(clientsCountMap).map(option => (
                                            <button
                                              key={option}
                                              onClick={() => toggleClientFilter(option)}
                                              className={`flex items-center justify-between w-full text-left px-2 py-1 text-xs rounded transition-colors
                                                ${clientFilter.includes(option) ? 'bg-amber-100 text-amber-800' : 'text-gray-500 hover:text-gray-800'}
                                              `}
                                            >
                                              <span className="flex items-center gap-2">
                                                {clientFilter.includes(option) ? (
                                                  <Check size={12} className="text-amber-600 font-bold flex-shrink-0" />
                                                ) : (
                                                  <div className="w-3 h-3 flex-shrink-0" />
                                                )}
                                                {option}
                                              </span>
                                              <span className="text-gray-400 text-[10px] font-medium">
                                                {clientsCountMap[option]}
                                              </span>
                                            </button>
                                          ))
                                        ) : (
                                          <div className="text-[10px] text-gray-400 px-2">No clients available</div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {showFilters === false && (
                            <div className="inline">
                              <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`p-1.5 rounded-md transition-colors profiler-bg text-white`}
                              >
                                <Filter size={16} />
                              </button>
                            </div>
                          )}
                          {/* MAIN CONTENT AREA */}
                          <div className="flex-1 ml-2 border-l border-gray-100 flex flex-col overflow-hidden max-h-[calc(100vh-150px)]">
                            {/* Table Container 2 */}

                            {/* Scrollable Table Section */}
                            <div className="flex-1 bg-white overflow-hidden flex flex-col">
                              <div className="overflow-auto flex-1 px-2 custom-table-scrollbar">
                                <table className="min-w-full table-fixed">
                                  <thead className="sticky top-0 z-20 text-gray-900 bg-white border-b border-[#C2C2C2]">
                                    <tr>
                                      {allColumns.map(
                                        (column) =>
                                          visibleColumns[column.key] && (
                                            <th
                                              key={column.key}
                                              className="px-2 py-2 text-left text-xs font-bold tracking-wider min-w-[11rem] cursor-pointer hover:bg-gray-50 group"
                                              onClick={() =>
                                                handleSort(column.key)
                                              }
                                            >
                                              <div className="flex items-center gap-1">
                                                {column.label}
                                                <div className="flex flex-col">
                                                  <svg
                                                    className={`w-2 h-2 ${sortBy === column.key && sortOrder === "asc" ? "text-[#025c43]" : "text-gray-300"} group-hover:text-gray-400`}
                                                    fill="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path d="M12 4l-8 8h16z" />
                                                  </svg>
                                                  <svg
                                                    className={`w-2 h-2 ${sortBy === column.key && sortOrder === "desc" ? "text-[#025c43]" : "text-gray-300"} group-hover:text-gray-400`}
                                                    fill="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path d="M12 20l8-8H4z" />
                                                  </svg>
                                                </div>
                                              </div>
                                            </th>
                                          ),
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white">
                                    {currentPageData.length > 0 ? (
                                      currentPageData.map((item) => (
                                        <tr
                                          key={item.id}
                                          className="hover:bg-gray-50 font-normal text-gray-900 transition-colors duration-150 px-2 py-2 text-xs border-none"
                                        >
                                          {visibleColumns.candidate && (
                                            <td className="px-2 py-2 text-xs min-w-[11rem]">
                                              {item.candidate}
                                            </td>
                                          )}
                                          {visibleColumns.job_name && (
                                            <td
                                              className="px-2 py-2 text-xs  truncate min-w-[11rem]"
                                              title={item.job_name}
                                            >
                                              {item.job_name === null
                                                ? "-"
                                                : item.job_name}
                                            </td>
                                          )}
                                          {visibleColumns.applicant_status && (
                                            <td className="px-2 py-2 text-xs min-w-[11rem]">
                                              <span
                                                className={`px-3 py-1 rounded-full text-xs`}
                                              >
                                                {capitalizeWords(
                                                  item.applicant_status,
                                                )}
                                              </span>
                                            </td>
                                          )}
                                          {visibleColumns.submitted_at && (
                                            <td className="px-2 py-2 text-xs min-w-[11rem]">
                                              {item.submitted_at === null
                                                ? "-"
                                                : item.submitted_at}
                                            </td>
                                          )}
                                          {visibleColumns.account_manager && (
                                            <td className="px-2 py-2 text-xs min-w-[11rem]">
                                              {item.account_manager === null
                                                ? "-"
                                                : item.account_manager}
                                            </td>
                                          )}
                                          {visibleColumns.sector && (
                                            <td className="px-2 py-2 text-xs min-w-[11rem]">
                                              {item.sector === null
                                                ? "-"
                                                : item.sector}
                                            </td>
                                          )}
                                          {visibleColumns.project && (
                                            <td className="px-2 py-2 text-xs min-w-[11rem]">
                                              {item.project === null
                                                ? "-"
                                                : item.project}
                                            </td>
                                          )}
                                          {visibleColumns.client && (
                                            <td className="px-2 py-2 text-xs min-w-[11rem]">
                                              {item.client === null
                                                ? "-"
                                                : item.client}
                                            </td>
                                          )}
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td
                                          colSpan={
                                            Object.values(
                                              visibleColumns,
                                            ).filter(Boolean).length
                                          }
                                          className="px-2 py-12 text-center text-xs text-gray-500"
                                        >
                                          No Job Applications found matching
                                          your criteria
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {/* Pagination */}
                            {totalPages > 0 && (
                              <div className="mt-4 flex items-center justify-between px-4 bg-white flex-shrink-0">
                                <div className="text-sm text-gray-600">
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs text-gray-600">
                                      Showing
                                    </span>

                                    <span className="text-xs text-gray-600">
                                      {/* Calculate range manually for better UX */}
                                      {totalForPagination > 0 ? offset + 1 : 0}{" "}
                                      to{" "}
                                      {Math.min(
                                        offset + pageSize,
                                        totalForPagination,
                                      )}{" "}
                                      of{" "}
                                      <span className="font-semibold text-gray-800">
                                        {totalForPagination}
                                      </span>{" "}
                                      records
                                    </span>

                                    <select
                                      value={pageSize}
                                      onChange={(e) => {
                                        const newPageSize = Number(
                                          e.target.value,
                                        );
                                        setPageSize(newPageSize);
                                        setCurrentPage(1);
                                        setOffset(0);
                                      }}
                                      className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                      <option value={10}>10</option>
                                      <option value={25}>25</option>
                                      <option value={50}>50</option>
                                      <option value={100}>100</option>
                                    </select>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      if (currentPage > 1) {
                                        const newPage = currentPage - 1;
                                        setCurrentPage(newPage);
                                        const newOffset =
                                          (newPage - 1) * pageSize;
                                        setOffset(newOffset);
                                      }
                                    }}
                                    disabled={currentPage === 1}
                                    className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 
                                rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed 
                                transition-colors duration-200 disabled:opacity-50"
                                  >
                                    Prev
                                  </button>
                                  <div className="flex items-center gap-1">
                                    {Array.from(
                                      { length: Math.min(5, totalPages) },
                                      (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                          pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                          pageNum = i + 1;
                                        } else if (
                                          currentPage >=
                                          totalPages - 2
                                        ) {
                                          pageNum = totalPages - 4 + i;
                                        } else {
                                          pageNum = currentPage - 2 + i;
                                        }
                                        return (
                                          <button
                                            key={pageNum}
                                            onClick={() => {
                                              setCurrentPage(pageNum);
                                              const newOffset =
                                                (pageNum - 1) * pageSize;
                                              setOffset(newOffset);
                                            }}
                                            className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors duration-200 ${
                                              currentPage === pageNum
                                                ? "profiler-bg text-white"
                                                : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                                            }`}
                                          >
                                            {pageNum}
                                          </button>
                                        );
                                      },
                                    )}
                                  </div>
                                  <button
                                    onClick={() => {
                                      if (currentPage < totalPages) {
                                        const newPage = currentPage + 1;
                                        setCurrentPage(newPage);
                                        const newOffset =
                                          (newPage - 1) * pageSize;
                                        setOffset(newOffset);
                                      }
                                    }}
                                    disabled={currentPage === totalPages}
                                    className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 
                                rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed 
                                transition-colors duration-200 disabled:opacity-50"
                                  >
                                    Next
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {}
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LeaderBoard;
